import { motion } from "framer-motion";
import { ExpandIcon } from "@rosette/web_components/src/icon/ExpandIcon.js";
import { fadeInOut } from "@/utils/fadeInOut.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import type { StaticImageData } from "next/image";
import Image from "next/image";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";


type OptionMode = "option" | "selection" | "selection-open";


export interface SelectorOption<V> {
    value: V;
    name: string;
    desc: string;
    icon: StaticImageData;
    disabled?: boolean;
}


interface GameSetupOptionProps<V> {
    className?: string;
    option: SelectorOption<V>;
    inlineIcon: boolean;
    mode?: OptionMode;
    onClick: () => void;
}

/**
 * An option to select while setting up your game.
 */
export function SelectorOptionElem<V>({
    className, onClick,
    option, mode, inlineIcon,
}: GameSetupOptionProps<V>) {
    mode = (mode === undefined ? "option" : mode);
    const isSelection = (mode !== "option");
    const disabled = !!option.disabled;

    // An open selection never lightens on hover, and a disabled option
    // only does when it is the closed selection.
    const nameHovers = (mode !== "selection-open" && (!disabled || mode === "selection"));

    return (
        <div className="flex">
            <div
                className={cn(
                    "group flex flex-wrap flex-row w-full max-w-[42em] cursor-pointer select-none",
                    "bg-primary border-b-[0.4rem] border-b-primary-strong",
                    // Darkens the fill only, as brightness-90 would also dim the hovered name.
                    "active:bg-[color-mix(in_srgb,var(--background-color-primary),var(--color-black)_10%)]",
                    "rounded-2xl shadow-[0_2px_4px_rgba(0,0,0,0.3)]",
                    className ?? null,
                )}
                onClick={onClick}>

                <motion.div
                    className="flex flex-row grow px-3 py-2 md:px-4 md:py-3"
                    {...fadeInOut(0.1)}>

                    {!inlineIcon && (
                        <div className={cn(
                            "h-full pt-2 pr-3 transition-transform duration-100 ease-out",
                            isSelection ? "scale-110" : null,
                            disabled ? "grayscale opacity-60" : null,
                        )}>
                            <Image
                                className={cn(
                                    "h-auto w-[3.5em] min-h-[3.5em] min-w-[3.5em] object-contain",
                                    "md:size-[4.5em] md:min-h-[4.5em] md:min-w-[4.5em]",
                                )}
                                src={option.icon}
                                alt={option.name} />
                        </div>
                    )}
                    <div className="w-full">
                        <div className={cn(
                            "flex flex-row items-end w-full font-bold text-2xl",
                            disabled ? "text-subtle" : "text-on-primary",
                            nameHovers ? "group-hover:text-strong" : null,
                        )}>
                            <span>
                                {option.name}
                                {option.disabled && " (Unavailable)"}
                            </span>
                            {inlineIcon && (
                                <InlineIcon
                                    size="lg"
                                    className={cn(
                                    "ml-2",
                                    disabled ? "grayscale opacity-60" : null,
                                )}>
                                    <Image src={option.icon} alt={option.name} />
                                </InlineIcon>
                            )}

                            {isSelection && (
                                <div className={cn(
                                    "flex flex-row grow-0 self-start ml-auto py-1 pl-3",
                                    "text-xl font-bold leading-[1.1em]",
                                )}>
                                    <ExpandIcon className={cn(
                                        "fill-on-primary size-(--text-2xl) min-w-(--text-2xl) min-h-(--text-2xl)",
                                        "transition-transform duration-200 ease-in-out",
                                        mode === "selection-open" ? "-scale-100" : "scale-100",
                                    )} />
                                </div>
                            )}
                        </div>
                        <div className={cn(
                            "text-base pr-3",
                            disabled ? "text-subtle" : "text-on-primary",
                        )}>
                            {option.desc}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
